import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchInteractionReports,
  fetchAttendanceReports,
  downloadInteractionsCsv,
  downloadAttendanceCsv,
  InteractionReportsResponse,
  AttendanceReportsResponse,
} from '../lib/api';
import {
  BarChart3,
  Users,
  ScanLine,
  Radio,
  FileSpreadsheet,
  Download,
  RefreshCw,
  TrendingUp,
  Award,
  Building2,
  Calendar,
  Layers,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Sparkles,
} from 'lucide-react';

interface AdminReportsAnalyticsProps {
  adminKey: string;
}

export function AdminReportsAnalytics({ adminKey }: AdminReportsAnalyticsProps) {
  const [activeDemographicTab, setActiveDemographicTab] = useState<'tier' | 'role'>('tier');
  const [isExportingInteractions, setIsExportingInteractions] = useState(false);
  const [isExportingAttendance, setIsExportingAttendance] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 1. Fetch Interactions Report
  const {
    data: interactionsData,
    isLoading: isLoadingInteractions,
    isError: isErrorInteractions,
    error: errorInteractions,
    refetch: refetchInteractions,
    isFetching: isFetchingInteractions,
  } = useQuery<InteractionReportsResponse>({
    queryKey: ['admin-reports-interactions', adminKey],
    queryFn: () => fetchInteractionReports(adminKey),
    enabled: !!adminKey,
    refetchInterval: 15000,
  });

  // 2. Fetch Attendance Report
  const {
    data: attendanceData,
    isLoading: isLoadingAttendance,
    isError: isErrorAttendance,
    error: errorAttendance,
    refetch: refetchAttendance,
    isFetching: isFetchingAttendance,
  } = useQuery<AttendanceReportsResponse>({
    queryKey: ['admin-reports-attendance', adminKey],
    queryFn: () => fetchAttendanceReports(adminKey),
    enabled: !!adminKey,
    refetchInterval: 15000,
  });

  const handleRefresh = async () => {
    await Promise.all([refetchInteractions(), refetchAttendance()]);
    showToast('success', 'Turnout & interaction metrics refreshed');
  };

  const showToast = (type: 'success' | 'error', text: string) => {
    setActionMessage({ type, text });
    setTimeout(() => {
      setActionMessage(null);
    }, 4000);
  };

  const handleExportInteractions = async () => {
    try {
      setIsExportingInteractions(true);
      await downloadInteractionsCsv(adminKey);
      showToast('success', 'Interaction logs exported successfully (CSV)');
    } catch (err: any) {
      showToast('error', err?.message || 'Failed to export interactions CSV');
    } finally {
      setIsExportingInteractions(false);
    }
  };

  const handleExportAttendance = async () => {
    try {
      setIsExportingAttendance(true);
      await downloadAttendanceCsv(adminKey);
      showToast('success', 'Attendance & turnout roster exported successfully (CSV)');
    } catch (err: any) {
      showToast('error', err?.message || 'Failed to export attendance CSV');
    } finally {
      setIsExportingAttendance(false);
    }
  };

  const isGlobalLoading = isLoadingInteractions && isLoadingAttendance;
  const isGlobalError = isErrorInteractions && isErrorAttendance;

  if (isGlobalLoading) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200/80 p-12 text-center space-y-4 shadow-xs">
        <div className="w-12 h-12 rounded-2xl bg-[#0A3D27]/10 text-[#0A3D27] flex items-center justify-center mx-auto animate-spin">
          <RefreshCw className="w-6 h-6" />
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-bold text-slate-800">Calculating Turnout &amp; Interaction Velocity</h3>
          <p className="text-xs text-slate-500">Aggregating real-time QR scans, NFC taps, and gate timestamps...</p>
        </div>
      </div>
    );
  }

  if (isGlobalError) {
    return (
      <div className="bg-red-50 rounded-3xl border border-red-200 p-8 text-center space-y-3">
        <AlertTriangle className="w-8 h-8 text-red-600 mx-auto" />
        <h3 className="text-sm font-bold text-red-900">Failed to load reports</h3>
        <p className="text-xs text-red-700">
          {(errorInteractions as Error)?.message || (errorAttendance as Error)?.message || 'Unauthorized or service error'}
        </p>
        <button
          type="button"
          onClick={handleRefresh}
          className="px-4 py-2 bg-white text-red-700 border border-red-300 rounded-xl text-xs font-bold hover:bg-red-50 cursor-pointer"
        >
          Retry Fetching Reports
        </button>
      </div>
    );
  }

  const funnel = interactionsData?.funnel || {
    totalRegistered: attendanceData?.totalRegistered || 0,
    badgeScanned: attendanceData?.totalCheckedIn || 0,
    exhibitorConnected: 0,
    meetingScheduled: 0,
  };

  const scannedRate =
    funnel.totalRegistered > 0 ? Math.round((funnel.badgeScanned / funnel.totalRegistered) * 100) : 0;
  const connectedRate =
    funnel.badgeScanned > 0 ? Math.round((funnel.exhibitorConnected / funnel.badgeScanned) * 100) : 0;
  const meetingRate =
    funnel.exhibitorConnected > 0 ? Math.round((funnel.meetingScheduled / funnel.exhibitorConnected) * 100) : 0;

  const qrPercentage = interactionsData?.qrVsNfc?.qrPercentage ?? 0;
  const nfcPercentage = interactionsData?.qrVsNfc?.nfcPercentage ?? 0;
  const qrCount = interactionsData?.qrVsNfc?.qrCount ?? 0;
  const nfcCount = interactionsData?.qrVsNfc?.nfcCount ?? 0;

  return (
    <div className="space-y-6">
      {/* Toast Feedback */}
      {actionMessage && (
        <div
          className={`p-3.5 rounded-2xl border text-xs font-bold flex items-center justify-between transition-all animate-in fade-in slide-in-from-top-2 ${
            actionMessage.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {actionMessage.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
            )}
            <span>{actionMessage.text}</span>
          </div>
          <button
            type="button"
            onClick={() => setActionMessage(null)}
            className="text-slate-400 hover:text-slate-700 ml-4 cursor-pointer text-xs"
          >
            ✕
          </button>
        </div>
      )}

      {/* Control Header & Live Actions */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#0A3D27]/10 text-[#0A3D27] text-[11px] font-bold">
              <BarChart3 className="w-3.5 h-3.5" />
              <span>Turnout &amp; Interaction Velocity</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#FAF5EA] border border-[#E6C687] text-[#785613] text-[11px] font-bold">
              <Calendar className="w-3 h-3 text-[#785613]" />
              <span>27–28 July 2027 • Jio World Convention Centre BKC</span>
            </span>
          </div>
          <h3 className="text-base font-black text-slate-900">
            {attendanceData?.eventName || interactionsData?.eventName || 'India International Agri Business Expo (IIAB 2027)'}
          </h3>
          <p className="text-xs text-slate-500">
            Real-time ingress turnstiles, badge handovers, QR/NFC connection exchange velocity, and exhibitor pipeline.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetchingInteractions || isFetchingAttendance}
            className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-2 cursor-pointer disabled:opacity-50"
            title="Refresh analytics data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetchingInteractions || isFetchingAttendance ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <button
            type="button"
            onClick={handleExportInteractions}
            disabled={isExportingInteractions}
            className="px-3.5 py-2.5 bg-white hover:bg-slate-50 text-[#0A3D27] border border-[#0A3D27]/40 rounded-xl text-xs font-bold transition-all shadow-2xs hover:shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 text-[#0A3D27]" />
            <span>{isExportingInteractions ? 'Exporting...' : 'Export Interactions (CSV)'}</span>
          </button>

          <button
            type="button"
            onClick={handleExportAttendance}
            disabled={isExportingAttendance}
            className="px-4 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs hover:shadow-sm flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5 text-[#E6C687]" />
            <span>{isExportingAttendance ? 'Exporting...' : 'Export Turnout Roster (CSV)'}</span>
          </button>
        </div>
      </div>

      {/* TOP ROW: 4 PRIMARY METRIC TILES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* KPI 1: Overall Turnout */}
        <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Turnout Rate</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-slate-900 tracking-tight">
                {attendanceData?.turnoutRatePercent ?? 0}%
              </span>
              <span className="text-xs font-medium text-slate-500">
                ({attendanceData?.totalCheckedIn ?? 0} of {attendanceData?.totalRegistered ?? 0})
              </span>
            </div>
            {/* Turnout Progress Bar */}
            <div className="w-full bg-slate-100 rounded-full h-2 mt-2.5 overflow-hidden">
              <div
                className="bg-[#0A3D27] h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, attendanceData?.turnoutRatePercent ?? 0)}%` }}
              />
            </div>
          </div>
          <div className="pt-1 text-[11px] text-slate-500 flex items-center justify-between">
            <span>Checked-in badges</span>
            <span className="font-bold text-slate-700">{attendanceData?.totalCheckedIn ?? 0}</span>
          </div>
        </div>

        {/* KPI 2: Total Onsite Interactions */}
        <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Total Interactions</span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-slate-900 tracking-tight">
                {interactionsData?.totalInteractions ?? 0}
              </span>
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/60">
                Live
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Badge scans, lead tags &amp; mutual connections</p>
          </div>
          <div className="pt-1 text-[11px] text-slate-500 flex items-center justify-between border-t border-slate-100">
            <span>QR vs NFC Split</span>
            <span className="font-bold text-[#0A3D27]">
              {qrPercentage}% QR • {nfcPercentage}% NFC
            </span>
          </div>
        </div>

        {/* KPI 3: Daily Ingress Breakdown (Day 1 vs Day 2) */}
        <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Daily Ingress</span>
            <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
              <Calendar className="w-4 h-4" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#0A3D27]" />
                <span className="text-xs font-bold text-slate-800">Day 1 (27 July)</span>
              </div>
              <span className="text-sm font-black text-slate-900">{attendanceData?.uniqueAttendeesDay1 ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-xs font-bold text-slate-800">Day 2 (28 July)</span>
              </div>
              <span className="text-sm font-black text-slate-900">{attendanceData?.uniqueAttendeesDay2 ?? 0}</span>
            </div>
          </div>
          <div className="pt-1 text-[11px] text-slate-500 flex items-center justify-between border-t border-slate-100">
            <span>Peak Gate Turnout</span>
            <span className="font-bold text-slate-700">
              {Math.max(attendanceData?.uniqueAttendeesDay1 ?? 0, attendanceData?.uniqueAttendeesDay2 ?? 0)} attendees
            </span>
          </div>
        </div>

        {/* KPI 4: Physical Badge Handovers */}
        <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs space-y-3">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Badges Handed Over</span>
            <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black text-slate-900 tracking-tight">
                {attendanceData?.totalHandovers ?? 0}
              </span>
              <span className="text-xs font-medium text-slate-500">
                physical 4×6 badges
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Collected at registration desk consoles</p>
          </div>
          <div className="pt-1 text-[11px] text-slate-500 flex items-center justify-between border-t border-slate-100">
            <span>Handover Compliance</span>
            <span className="font-bold text-purple-800">
              {attendanceData?.totalCheckedIn
                ? Math.round(((attendanceData.totalHandovers || 0) / attendanceData.totalCheckedIn) * 100)
                : 0}
              % of arrivals
            </span>
          </div>
        </div>
      </div>

      {/* SECTION 2: VISITOR-TO-EXHIBITOR CONVERSION FUNNEL */}
      <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#0A3D27] block">
              Engagement Lifecycle
            </span>
            <h4 className="text-lg font-black text-slate-900 tracking-tight">
              Visitor-to-Exhibitor Conversion Funnel
            </h4>
            <p className="text-xs text-slate-500">
              Tracking how registered participants transition from gate turnstiles into stall connections and booked meetings.
            </p>
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold shrink-0">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-700" />
            <span>High Intent Ingress</span>
          </div>
        </div>

        {/* Funnel Step Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Step 1: Registered */}
          <div className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 space-y-2 relative">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="font-bold text-slate-600">1. Total Registered</span>
              <span className="text-[10px] font-black uppercase text-slate-400">Baseline</span>
            </div>
            <div className="text-2xl font-black text-slate-900">{funnel.totalRegistered}</div>
            <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
              <div className="bg-slate-700 h-full rounded-full w-full" />
            </div>
            <p className="text-[11px] text-slate-500">100% registration base</p>
          </div>

          {/* Step 2: Ingress Turnstiles */}
          <div className="bg-[#FAF5EA] border border-[#E6C687]/60 rounded-2xl p-4 space-y-2 relative">
            <div className="flex items-center justify-between text-xs text-[#785613]">
              <span className="font-bold">2. Gate Checked-In</span>
              <span className="text-[10px] font-black uppercase bg-[#FAF5EA] px-1.5 py-0.5 rounded border border-[#E6C687]">
                {scannedRate}%
              </span>
            </div>
            <div className="text-2xl font-black text-slate-900">{funnel.badgeScanned}</div>
            <div className="w-full bg-amber-200/60 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-amber-600 h-full rounded-full transition-all"
                style={{ width: `${Math.min(100, scannedRate)}%` }}
              />
            </div>
            <p className="text-[11px] text-slate-600">Turnstile scanned badge ingress</p>
          </div>

          {/* Step 3: Exhibitor Connected */}
          <div className="bg-emerald-50/50 border border-emerald-200/80 rounded-2xl p-4 space-y-2 relative">
            <div className="flex items-center justify-between text-xs text-emerald-800">
              <span className="font-bold">3. Booth Connected</span>
              <span className="text-[10px] font-black uppercase bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-300">
                {connectedRate}%
              </span>
            </div>
            <div className="text-2xl font-black text-slate-900">{funnel.exhibitorConnected}</div>
            <div className="w-full bg-emerald-200/60 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-[#0A3D27] h-full rounded-full transition-all"
                style={{ width: `${Math.min(100, connectedRate)}%` }}
              />
            </div>
            <p className="text-[11px] text-slate-600">Exhibitor badge scan or note taken</p>
          </div>

          {/* Step 4: B2B Meeting Scheduled */}
          <div className="bg-purple-50/50 border border-purple-200/80 rounded-2xl p-4 space-y-2 relative">
            <div className="flex items-center justify-between text-xs text-purple-800">
              <span className="font-bold">4. B2B Meetings Booked</span>
              <span className="text-[10px] font-black uppercase bg-purple-100 px-1.5 py-0.5 rounded border border-purple-300">
                {meetingRate}%
              </span>
            </div>
            <div className="text-2xl font-black text-slate-900">{funnel.meetingScheduled}</div>
            <div className="w-full bg-purple-200/60 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-purple-700 h-full rounded-full transition-all"
                style={{ width: `${Math.min(100, meetingRate)}%` }}
              />
            </div>
            <p className="text-[11px] text-slate-600">Confirmed 30-min meeting slots</p>
          </div>
        </div>
      </div>

      {/* SECTION 3: SCAN VELOCITY & TECHNOLOGY BREAKDOWN */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* QR vs NFC Split */}
        <div className="bg-white border border-slate-200/90 rounded-2xl p-6 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">Ingress Hardware</span>
              <h4 className="text-sm font-black text-slate-900">QR Code vs NFC Tap</h4>
            </div>
            <Radio className="w-4 h-4 text-[#0A3D27]" />
          </div>

          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-bold text-slate-800">
                <span className="flex items-center gap-1.5">
                  <ScanLine className="w-3.5 h-3.5 text-[#0A3D27]" />
                  <span>Optical QR Code Scans</span>
                </span>
                <span>
                  {qrCount} ({qrPercentage}%)
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-[#0A3D27] h-full rounded-full transition-all"
                  style={{ width: `${qrPercentage}%` }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-bold text-slate-800">
                <span className="flex items-center gap-1.5">
                  <Radio className="w-3.5 h-3.5 text-amber-600" />
                  <span>NFC 13.56 MHz Badge Taps</span>
                </span>
                <span>
                  {nfcCount} ({nfcPercentage}%)
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-amber-500 h-full rounded-full transition-all"
                  style={{ width: `${nfcPercentage}%` }}
                />
              </div>
            </div>
          </div>

          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-[11px] text-slate-600 leading-relaxed">
            NFC badges allow instantaneous &lt;150ms desk handovers and contact exchange at exhibitor stalls without camera focusing.
          </div>
        </div>

        {/* Hourly Scan Velocity Timeline */}
        <div className="lg:col-span-2 bg-white border border-slate-200/90 rounded-2xl p-6 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">Real-time Ingress</span>
              <h4 className="text-sm font-black text-slate-900">Hourly Scan Velocity (10:00 AM – 06:00 PM)</h4>
            </div>
            <Clock className="w-4 h-4 text-slate-400" />
          </div>

          {interactionsData?.hourlyVelocity && interactionsData.hourlyVelocity.length > 0 ? (
            <div className="pt-2">
              <div className="h-32 flex items-end gap-2 border-b border-slate-200 pb-2">
                {interactionsData.hourlyVelocity.map((slot, i) => {
                  const maxScans = Math.max(1, ...interactionsData.hourlyVelocity.map((h) => h.count));
                  const heightPercent = Math.max(8, Math.round((slot.count / maxScans) * 100));

                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                      <div className="text-[10px] font-bold text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">
                        {slot.count}
                      </div>
                      <div className="w-full bg-slate-100 rounded-t-lg overflow-hidden flex items-end h-24">
                        <div
                          className="w-full bg-[#0A3D27] group-hover:bg-[#062B1B] rounded-t-md transition-all duration-300"
                          style={{ height: `${heightPercent}%` }}
                          title={`${slot.hour}: ${slot.count} scans`}
                        />
                      </div>
                      <span className="text-[10px] font-medium text-slate-500 truncate w-full text-center">
                        {slot.hour}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="pt-2 flex items-center justify-between text-[11px] text-slate-500">
                <span>Pavilion 3 operational gate hours</span>
                <span className="font-bold text-slate-700">Expo Closes: 06:00 PM</span>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400 text-xs">
              No hourly scan velocity data recorded yet for active sessions.
            </div>
          )}
        </div>
      </div>

      {/* SECTION 4: LEADERBOARDS (TOP CONNECTED PARTICIPANTS & TOP BOOTHS) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaderboard A: Top 10 Most Connected Attendees */}
        <div className="bg-white border border-slate-200/90 rounded-2xl shadow-xs overflow-hidden flex flex-col">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-[#FAF5EA] border border-[#E6C687] text-[#785613] flex items-center justify-center">
                <Award className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-sm font-black text-slate-900">Top Connected Delegates &amp; Staff</h4>
                <p className="text-[11px] text-slate-500">Highest mutual badge scans and business exchanges</p>
              </div>
            </div>
            <span className="text-xs font-bold text-slate-400">Ranked #1–10</span>
          </div>

          <div className="divide-y divide-slate-100 flex-1 overflow-x-auto">
            {interactionsData?.topPeople && interactionsData.topPeople.length > 0 ? (
              interactionsData.topPeople.map((person, idx) => (
                <div key={person.attendeeId || idx} className="p-3.5 px-5 flex items-center justify-between hover:bg-slate-50/80 transition-colors">
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-6 h-6 rounded-full text-xs font-black flex items-center justify-center ${
                        idx === 0
                          ? 'bg-[#FAF5EA] text-[#785613] border border-[#E6C687]'
                          : idx === 1
                          ? 'bg-slate-200 text-slate-700'
                          : idx === 2
                          ? 'bg-amber-100 text-amber-800'
                          : 'text-slate-400'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-900">{person.fullName}</span>
                        <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-slate-100 text-slate-600 uppercase">
                          {person.tier || person.primaryRole}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 truncate max-w-xs">{person.companyName || 'Private Participant'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-black text-[#0A3D27]">{person.totalInteractions}</span>
                    <span className="text-[10px] text-slate-400 block">connections</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-12 text-center text-slate-400 text-xs">
                No participant interaction logs recorded yet.
              </div>
            )}
          </div>
        </div>

        {/* Leaderboard B: Top 10 Exhibitor Booths by Engagement */}
        <div className="bg-white border border-slate-200/90 rounded-2xl shadow-xs overflow-hidden flex flex-col">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center justify-center">
                <Building2 className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-sm font-black text-slate-900">Top Exhibitor Booths by Engagement</h4>
                <p className="text-[11px] text-slate-500">Stalls capturing the highest visitor footfall and badge scans</p>
              </div>
            </div>
            <span className="text-xs font-bold text-slate-400">Ranked #1–10</span>
          </div>

          <div className="divide-y divide-slate-100 flex-1 overflow-x-auto">
            {interactionsData?.topStalls && interactionsData.topStalls.length > 0 ? (
              interactionsData.topStalls.map((booth, idx) => (
                <div key={booth.boothId || idx} className="p-3.5 px-5 flex items-center justify-between hover:bg-slate-50/80 transition-colors">
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-6 h-6 rounded-full text-xs font-black flex items-center justify-center ${
                        idx === 0
                          ? 'bg-[#FAF5EA] text-[#785613] border border-[#E6C687]'
                          : idx === 1
                          ? 'bg-slate-200 text-slate-700'
                          : idx === 2
                          ? 'bg-amber-100 text-amber-800'
                          : 'text-slate-400'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-900">{booth.companyName}</span>
                        <span className="px-1.5 py-0.2 rounded text-[10px] font-black bg-[#0A3D27]/10 text-[#0A3D27]">
                          Booth {booth.boothId}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-black text-[#0A3D27]">{booth.count}</span>
                    <span className="text-[10px] text-slate-400 block">scans logged</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-12 text-center text-slate-400 text-xs">
                No booth lead capture interactions recorded yet.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SECTION 5: DEMOGRAPHIC TURNOUT BREAKDOWNS (ROLE & TIER) */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-6 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">Turnout Breakdown</span>
            <h4 className="text-sm font-black text-slate-900">Demographic Ingress Rates</h4>
          </div>

          {/* Sub-tabs: By Pass Tier vs By Staff Role */}
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setActiveDemographicTab('tier')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeDemographicTab === 'tier' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              By Pass Tier
            </button>
            <button
              type="button"
              onClick={() => setActiveDemographicTab('role')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeDemographicTab === 'role' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              By Participant Role
            </button>
          </div>
        </div>

        {/* Tab Content: By Tier */}
        {activeDemographicTab === 'tier' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
            {attendanceData?.byTier && attendanceData.byTier.length > 0 ? (
              attendanceData.byTier.map((item) => (
                <div key={item.tier} className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-slate-900">{item.label}</span>
                    <span className="text-xs font-bold text-[#0A3D27]">{item.attendancePercent}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-[#0A3D27] h-full rounded-full transition-all"
                      style={{ width: `${Math.min(100, item.attendancePercent)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                    <span>Turnout:</span>
                    <span className="font-bold text-slate-700">
                      {item.checkedIn} / {item.totalRegistered} registered
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 py-6 text-center text-slate-400 text-xs">
                No tier data available.
              </div>
            )}
          </div>
        )}

        {/* Tab Content: By Role */}
        {activeDemographicTab === 'role' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-1">
            {attendanceData?.byRole && attendanceData.byRole.length > 0 ? (
              attendanceData.byRole.map((item) => (
                <div key={item.role} className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-slate-900">{item.label}</span>
                    <span className="text-xs font-bold text-purple-700">{item.attendancePercent}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-purple-700 h-full rounded-full transition-all"
                      style={{ width: `${Math.min(100, item.attendancePercent)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                    <span>Turnout:</span>
                    <span className="font-bold text-slate-700">
                      {item.checkedIn} / {item.totalRegistered}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-4 py-6 text-center text-slate-400 text-xs">
                No role data available.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminReportsAnalytics;
